import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
    MenuItem, Skeleton, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ScheduleSendOutlinedIcon from "@mui/icons-material/ScheduleSendOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import DateRangeOutlinedIcon from "@mui/icons-material/DateRangeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ToggleOnOutlinedIcon from "@mui/icons-material/ToggleOnOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useSnackbar } from "notistack";
import PageTitle from "../PageTitle";
import { apiConstants, deleteGeneric, getGeneric, postGeneric, putGeneric } from "../../common/constants";
import { formatDate } from "../../helpers/UtilityHelper";
import EscalationV2Nav from "./EscalationV2Nav";
import EmsPageHeader from "./EmsPageHeader";
import { MUTED, NAVY, gradientBtnSx } from "./SectionCard";
import { emailProblem, splitEmails } from "./emailValidation";
import { ACCENTS, CARD_SX, COLORS, rise } from "./emsTheme";
import {
    ConfigEmpty, ConfigStat, EmailChips, Eyebrow, IconTile, OFF_GREY, Pill, Segmented, StatePill, cancelBtnSx, purpleControlsSx, textBtnSx, topBarCardSx,
} from "./ConfigUi";

const API = apiConstants.ESCALATION_REPORT_SCHEDULES_V2;
const errMsg = (e, fallback) => {
    const m = e?.response?.data?.message || (typeof e?.response?.data === "string" ? e.response.data : "");
    return m ? String(m).replace(/^(Something went wrong-|Input Data Error:)\s*/, "") : fallback;
};

const FREQUENCIES = [
    { value: "DAILY", label: "Daily", covers: "the previous day", accent: "blue", icon: TodayOutlinedIcon },
    { value: "WEEKLY", label: "Weekly", covers: "the previous Monday to Sunday", accent: "purple", icon: DateRangeOutlinedIcon },
    { value: "MONTHLY", label: "Monthly", covers: "the previous calendar month", accent: "green", icon: CalendarMonthOutlinedIcon },
];
const freqOf = (value) => FREQUENCIES.find((f) => f.value === value)
    || { value, label: value || "Schedule", covers: "", accent: "purple", icon: ScheduleOutlinedIcon };
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const ordinal = (n) => `${n}${["th", "st", "nd", "rd"][((n % 100) - 20) % 10] || ["th", "st", "nd", "rd"][n % 100] || "th"}`;

const wrapPillSx = { whiteSpace: "normal", textAlign: "left", borderRadius: "10px" };

/** "Weekly, Mondays at 10:00" — how a schedule reads in the list. */
const describe = (s) => {
    const at = `at ${s.sendTime || "10:00"} IST`;
    if (s.frequency === "DAILY") return `Daily ${at}`;
    if (s.frequency === "WEEKLY") return `Weekly, ${WEEKDAYS[(s.dayOfWeek || 1) - 1]}s ${at}`;
    return `Monthly, on the ${ordinal(s.dayOfMonth || 1)} ${at}`;
};

const empty = { name: "", frequency: "WEEKLY", dayOfWeek: 1, dayOfMonth: 1, sendTime: "10:00", recipients: "", enabled: true };

const dialogPaperSx = { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: "calc(100% - 64px)" } };

const ScheduleDialog = ({ open, original, onClose, onSaved }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setForm(original ? {
            name: original.name || "",
            frequency: original.frequency || "WEEKLY",
            dayOfWeek: original.dayOfWeek || 1,
            dayOfMonth: original.dayOfMonth || 1,
            sendTime: original.sendTime || "10:00",
            recipients: (original.recipients || []).join(", "),
            enabled: original.enabled !== false,
        } : empty);
    }, [open, original]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const emails = splitEmails(form.recipients);
    const emailIssue = emails.map(emailProblem).find(Boolean);
    const freq = FREQUENCIES.find((f) => f.value === form.frequency);
    const invalid = !form.name.trim() || emails.length === 0 || !!emailIssue || !/^\d{2}:\d{2}$/.test(form.sendTime);

    const save = async () => {
        setSaving(true);
        const body = {
            name: form.name.trim(),
            frequency: form.frequency,
            dayOfWeek: form.frequency === "WEEKLY" ? Number(form.dayOfWeek) : null,
            dayOfMonth: form.frequency === "MONTHLY" ? Number(form.dayOfMonth) : null,
            sendTime: form.sendTime,
            recipients: emails,
            enabled: form.enabled,
        };
        try {
            if (original) await putGeneric(`${API}/${original.id}`, body);
            else await postGeneric(API, body);
            enqueueSnackbar(original ? "Schedule saved" : "Schedule created", { variant: "success" });
            onSaved();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Couldn't save the schedule"), { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
                <IconTile icon={freqOf(form.frequency).icon} accent={ACCENTS[freqOf(form.frequency).accent]} />
                <Box sx={{ minWidth: 0 }}>
                    <Eyebrow color={ACCENTS[freqOf(form.frequency).accent].solid}>Report schedule</Eyebrow>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: NAVY, lineHeight: 1.3, overflowWrap: "anywhere" }}>
                        {original ? `Edit ${original.name}` : "New report schedule"}
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important", ...purpleControlsSx }}>
                <TextField label="Name" size="small" value={form.name} onChange={(e) => set("name", e.target.value)}
                    placeholder="e.g. Weekly ops summary" inputProps={{ maxLength: 80 }} />
                <Box>
                    <Typography id="rs-how-often" sx={{ fontSize: 12, fontWeight: 700, color: MUTED, mb: 0.75 }}>How often</Typography>
                    <Segmented ariaLabel="How often" value={form.frequency} onChange={(v) => set("frequency", v)}
                        options={FREQUENCIES.map((f) => ({ value: f.value, label: f.label, accent: f.accent }))} />
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                    {form.frequency === "WEEKLY" && (
                        <TextField select label="On" size="small" value={form.dayOfWeek} onChange={(e) => set("dayOfWeek", e.target.value)}>
                            {WEEKDAYS.map((d, i) => <MenuItem key={d} value={i + 1}>{d}</MenuItem>)}
                        </TextField>
                    )}
                    {form.frequency === "MONTHLY" && (
                        <TextField select label="Day of month" size="small" value={form.dayOfMonth} onChange={(e) => set("dayOfMonth", e.target.value)}>
                            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <MenuItem key={d} value={d}>{ordinal(d)}</MenuItem>)}
                        </TextField>
                    )}
                    <TextField label="Time (IST)" size="small" type="time" value={form.sendTime}
                        onChange={(e) => set("sendTime", e.target.value)} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
                </Box>
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", bgcolor: COLORS.ground, border: `1px solid ${COLORS.grid}`, borderRadius: "10px", p: 1.25, mt: -0.5 }}>
                    <ScheduleOutlinedIcon sx={{ fontSize: 16, color: MUTED, mt: 0.25 }} aria-hidden />
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                        Each report covers {freq?.covers}: tickets created, closed, open now and beyond TAT, by merchant, with the most overdue tickets.
                    </Typography>
                </Box>
                <TextField label="Send to" size="small" multiline minRows={2} value={form.recipients}
                    onChange={(e) => set("recipients", e.target.value)} placeholder="ops@prozo.com, lead@prozo.com"
                    error={!!emailIssue} helperText={emailIssue || "Prozo team members only. Separate addresses with commas."} />
                {emails.length > 0 && !emailIssue && (
                    <Box sx={{ mt: -1 }}><EmailChips emails={emails} /></Box>
                )}
                <Box sx={{ border: `1px solid ${COLORS.grid}`, borderRadius: "10px", px: 1.5, py: 0.75, bgcolor: form.enabled ? ACCENTS.green.soft : COLORS.ground,
                    transition: "background-color 150ms" }}>
                    <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />}
                        label={<Typography sx={{ fontSize: 14, fontWeight: 600, color: NAVY }}>{form.enabled ? "On: sends on schedule" : "Off: won't send until switched on"}</Typography>} />
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={cancelBtnSx}>Cancel</Button>
                <LoadingButton variant="contained" sx={gradientBtnSx} loading={saving} disabled={invalid} onClick={save}>
                    {original ? "Save" : "Create schedule"}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

const n = (v) => (v ?? 0).toLocaleString("en-IN");

const PREVIEW_TILES = [
    ["Created", "created", "purple"],
    ["Closed", "closed", "green"],
    ["Open now", "open", "blue"],
    ["Beyond TAT", "beyondTat", "red"],
];

const PreviewDialog = ({ schedule, onClose }) => {
    const { enqueueSnackbar } = useSnackbar();
    const [report, setReport] = useState(null);

    useEffect(() => {
        if (!schedule) { setReport(null); return; }
        postGeneric(`${API}/${schedule.id}/preview`, {})
            .then((res) => setReport(res?.data || {}))
            .catch((e) => { enqueueSnackbar(errMsg(e, "Couldn't build the preview"), { variant: "error" }); onClose(); });
    }, [schedule, enqueueSnackbar, onClose]);

    const t = report?.totals || {};
    return (
        <Dialog open={!!schedule} onClose={onClose} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <IconTile icon={VisibilityOutlinedIcon} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: NAVY, lineHeight: 1.3, overflowWrap: "anywhere" }}>{report?.subject || "Preview"}</Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>Nothing is sent from here. This is what the next email would contain.</Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                {!report ? (
                    <Box sx={{ display: "grid", gap: 1.5 }}>
                        <Typography sx={{ fontSize: 13, color: MUTED }}>Building the report…</Typography>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
                            {PREVIEW_TILES.map(([l]) => <Skeleton key={l} variant="rounded" height={76} />)}
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: "grid", gap: 2 }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
                            {PREVIEW_TILES.map(([l, key, accent]) => (
                                <Box key={l} sx={{ ...topBarCardSx(ACCENTS[accent]), boxShadow: "none", p: 1.5, pt: 2, textAlign: "center" }}>
                                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: NAVY, fontVariantNumeric: "tabular-nums" }}>{n(t[key])}</Typography>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: ACCENTS[accent].solid }}>{l}</Typography>
                                </Box>
                            ))}
                        </Box>
                        <Typography sx={{ fontSize: 13, color: MUTED }}>
                            To: {(report.recipients || []).join(", ") || "nobody yet"} · {n((report.merchants || []).length)} merchants · {n((report.overdueTickets || []).length)} overdue tickets listed
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}><Button onClick={onClose} sx={cancelBtnSx}>Close</Button></DialogActions>
        </Dialog>
    );
};

const actionBtnSx = (hover = ACCENTS.purple) => ({ color: COLORS.ink3, "&:hover": { color: hover.solid, bgcolor: hover.soft } });

/** One schedule: frequency badge, on/off switch, when it sends, who gets it, next and last send. */
const ScheduleCard = ({ s, busy, order, onToggle, onPreview, onSendNow, onEdit, onDelete }) => {
    const f = freqOf(s.frequency);
    const a = ACCENTS[f.accent];
    const off = s.enabled === false;
    const failed = s.lastOutcome === "FAILED";
    return (
        <Box sx={{ ...topBarCardSx(off ? OFF_GREY : a), display: "flex", flexDirection: "column", ...rise(order) }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, px: 2, pt: 2.5, pb: 1.5 }}>
                <Box sx={{ opacity: off ? 0.55 : 1, display: "flex" }}><IconTile icon={f.icon} accent={off ? OFF_GREY : a} size={40} /></Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>
                        <Pill accent={off ? OFF_GREY : a}>{f.label}</Pill>
                        <StatePill on={!off} />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: off ? MUTED : NAVY, mt: 0.5, lineHeight: 1.3, overflowWrap: "anywhere" }}>{s.name}</Typography>
                    <Typography sx={{ fontSize: 13, color: MUTED }}>
                        <ScheduleOutlinedIcon sx={{ fontSize: 15, verticalAlign: "-3px", mr: 0.5 }} aria-hidden />{s.scheduleLabel || describe(s)}
                    </Typography>
                </Box>
                <Box sx={purpleControlsSx}>
                    <Switch checked={s.enabled !== false} disabled={busy === `toggle-${s.id}`} onChange={() => onToggle(s)}
                        inputProps={{ "aria-label": `${s.name} on or off` }} />
                </Box>
            </Box>

            <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, mb: 0.5 }}>
                    Send to · {(s.recipients || []).length} recipient{(s.recipients || []).length === 1 ? "" : "s"}
                </Typography>
                {(s.recipients || []).length
                    ? <EmailChips emails={s.recipients} accent={off ? OFF_GREY : a} max={3} />
                    : <Typography sx={{ fontSize: 13, color: MUTED }}>Nobody yet</Typography>}
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1, px: 2, pb: 2 }}>
                <Box sx={{ bgcolor: COLORS.ground, borderRadius: "10px", p: 1.25, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Next send</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: off ? MUTED : NAVY }}>
                        {off ? "Off" : (s.nextRunAt ? formatDate(s.nextRunAt) : "-")}
                    </Typography>
                </Box>
                <Box sx={{ bgcolor: failed ? ACCENTS.red.soft : COLORS.ground, borderRadius: "10px", p: 1.25, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED }}>Last sent</Typography>
                    {s.lastSentAt ? (
                        <>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{formatDate(s.lastSentAt)}</Typography>
                            <Typography sx={{ fontSize: 12, color: failed ? ACCENTS.red.solid : MUTED, overflowWrap: "anywhere" }}>
                                {failed ? `Failed: ${s.lastError || "send error"}` : (s.lastPeriodLabel || "Sent")}
                            </Typography>
                        </>
                    ) : <Typography sx={{ fontSize: 13, color: MUTED }}>Not yet</Typography>}
                </Box>
            </Box>

            <Box sx={{ mt: "auto", display: "flex", alignItems: "center", gap: 0.5, px: 1.25, py: 0.75, borderTop: `1px solid ${COLORS.grid}`, bgcolor: "#FBFAFD" }}>
                <Button size="small" startIcon={<VisibilityOutlinedIcon />} sx={textBtnSx} onClick={() => onPreview(s)}>Preview</Button>
                <Box sx={{ flex: 1 }} />
                <Tooltip title="Send now"><span><IconButton size="small" aria-label={`Send ${s.name} now`} sx={actionBtnSx(ACCENTS.blue)}
                    disabled={busy === `send-${s.id}`} onClick={() => onSendNow(s)}><SendOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Edit"><IconButton size="small" aria-label={`Edit ${s.name}`} sx={actionBtnSx()} onClick={() => onEdit(s)}>
                    <EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Delete"><span><IconButton size="small" aria-label={`Delete ${s.name}`} sx={actionBtnSx(ACCENTS.red)}
                    disabled={busy === `delete-${s.id}`} onClick={() => onDelete(s)}><DeleteOutlineIcon fontSize="small" /></IconButton></span></Tooltip>
            </Box>
        </Box>
    );
};

/**
 * Report Schedules tab — internal summary emails (never to merchants). Each schedule has its own
 * frequency (daily / weekly / monthly), send time, recipients and on/off switch, so e.g. a weekly
 * report to the ops team and a monthly one to leadership can run side by side.
 */
const ReportSchedulesPage = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [schedules, setSchedules] = useState(null);
    const [editing, setEditing] = useState(undefined); // undefined = closed, null = new
    const [previewing, setPreviewing] = useState(null);
    const [busy, setBusy] = useState("");
    const [forbidden, setForbidden] = useState(false);
    const closePreview = useCallback(() => setPreviewing(null), []);

    const load = useCallback(async () => {
        try {
            const res = await getGeneric(API);
            setSchedules(res?.data || []);
        } catch (e) {
            if (e?.response?.status === 403 || /admin/i.test(errMsg(e, ""))) setForbidden(true);
            setSchedules([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (s) => {
        setBusy(`toggle-${s.id}`);
        try {
            await putGeneric(`${API}/${s.id}`, { ...s, enabled: !s.enabled });
            enqueueSnackbar(s.enabled ? `${s.name} switched off` : `${s.name} switched on`, { variant: "success" });
            load();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Couldn't change the schedule"), { variant: "error" });
        } finally { setBusy(""); }
    };
    const remove = async (s) => {
        if (!window.confirm(`Delete "${s.name}"? It won't send again.`)) return;
        setBusy(`delete-${s.id}`);
        try {
            await deleteGeneric(`${API}/${s.id}`);
            enqueueSnackbar(`${s.name} deleted`, { variant: "success" });
            load();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Couldn't delete the schedule"), { variant: "error" });
        } finally { setBusy(""); }
    };
    const sendNow = async (s) => {
        if (!window.confirm(`Send "${s.name}" now to ${(s.recipients || []).length} recipient(s)? The scheduled send still happens as usual.`)) return;
        setBusy(`send-${s.id}`);
        try {
            await postGeneric(`${API}/${s.id}/send-now`, {});
            enqueueSnackbar(`${s.name} sent`, { variant: "success" });
            load();
        } catch (e) {
            enqueueSnackbar(errMsg(e, "Couldn't send the report"), { variant: "error" });
        } finally { setBusy(""); }
    };

    // Summary from the list already loaded; the next send is the earliest nextRunAt of the ones switched on.
    const summary = useMemo(() => {
        const list = schedules || [];
        const on = list.filter((s) => s.enabled !== false);
        const next = on.filter((s) => s.nextRunAt && Number.isFinite(new Date(s.nextRunAt).getTime()))
            .sort((x, y) => new Date(x.nextRunAt).getTime() - new Date(y.nextRunAt).getTime())[0];
        return { total: list.length, on: on.length, next };
    }, [schedules]);

    const newBtn = (label = "New schedule") => (
        <Button variant="contained" startIcon={<AddIcon />} sx={gradientBtnSx} onClick={() => setEditing(null)}>{label}</Button>
    );

    return (
        <>
            <PageTitle>Escalation Management V2 — Report Schedules</PageTitle>
            <EmsPageHeader icon={ScheduleSendOutlinedIcon} title="Report Schedules"
                sub="Internal summary emails on a schedule. Each one has its own recipients and can be switched on or off."
                actions={!forbidden ? newBtn() : null} />
            <EscalationV2Nav />

            {forbidden ? (
                <ConfigEmpty icon={LockOutlinedIcon} accent={ACCENTS.orange} title="Admins only"
                    text="Only Proship admins or Escalation admins can view and change report schedules." />
            ) : (
                <>
                    {schedules !== null && schedules.length > 0 && (
                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" }, gap: { xs: 1.25, sm: 2 }, mb: 2 }}>
                            <ConfigStat icon={ScheduleSendOutlinedIcon} label="Schedules" value={summary.total} order={1}
                                caption={FREQUENCIES.map((f) => `${schedules.filter((s) => s.frequency === f.value).length} ${f.label.toLowerCase()}`).join(" · ")} />
                            <ConfigStat icon={ToggleOnOutlinedIcon} label="Switched on" accent="green" order={2}
                                value={`${summary.on} of ${summary.total}`} caption={summary.on === summary.total ? "all sending" : `${summary.total - summary.on} paused`} />
                            <Box sx={{ gridColumn: { xs: "1 / -1", md: "auto" }, minWidth: 0, display: "grid" }}>
                                <ConfigStat icon={EventAvailableOutlinedIcon} label="Next send" accent="blue" order={3}
                                    value={summary.next ? formatDate(summary.next.nextRunAt) : "-"}
                                    caption={summary.next ? summary.next.name : "nothing scheduled"} />
                            </Box>
                        </Box>
                    )}

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, px: 1.5, py: 1, borderRadius: "10px", bgcolor: COLORS.purpleSoft,
                        border: "1px solid #E2D7EE", ...rise(4) }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 18, color: COLORS.purple }} aria-hidden />
                        <Typography sx={{ fontSize: 13, color: COLORS.ink2 }}>Never sent to merchants. Each report covers the period just ended.</Typography>
                    </Box>

                    {schedules === null ? (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 2 }}>
                            {[0, 1, 2].map((i) => (
                                <Box key={i} sx={{ ...CARD_SX }} aria-busy="true">
                                    <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                                        <Skeleton variant="rounded" width={40} height={40} />
                                        <Box sx={{ flex: 1 }}><Skeleton width="35%" /><Skeleton width="70%" /></Box>
                                    </Box>
                                    <Skeleton width="50%" /><Skeleton variant="rounded" height={56} sx={{ mt: 1 }} />
                                </Box>
                            ))}
                        </Box>
                    ) : schedules.length === 0 ? (
                        <ConfigEmpty icon={ScheduleSendOutlinedIcon} title="No report schedules yet"
                            text="Create one, for example a weekly summary for the ops team, then add a monthly one for leadership."
                            action={newBtn("Create your first schedule")}>
                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 0.5 }}>
                                {FREQUENCIES.map((f) => (
                                    <Pill key={f.value} accent={ACCENTS[f.accent]} icon={f.icon} sx={wrapPillSx}>{f.label}: covers {f.covers}</Pill>
                                ))}
                            </Box>
                        </ConfigEmpty>
                    ) : (
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 340px), 1fr))", gap: 2 }}>
                            {schedules.map((s, i) => (
                                <ScheduleCard key={s.id} s={s} busy={busy} order={Math.min(i, 3) + 5}
                                    onToggle={toggle} onPreview={setPreviewing} onSendNow={sendNow} onEdit={setEditing} onDelete={remove} />
                            ))}
                        </Box>
                    )}
                </>
            )}

            <ScheduleDialog open={editing !== undefined} original={editing || null} onClose={() => setEditing(undefined)}
                onSaved={() => { setEditing(undefined); load(); }} />
            <PreviewDialog schedule={previewing} onClose={closePreview} />
        </>
    );
};

export default ReportSchedulesPage;
